import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { NavLink, generatePath, useNavigate } from 'react-router-dom';

import { DataContext, UserContext } from '../App';
import { Button, DeletePopup, Status } from '../components';
import { iconByWebsiteStatus } from '../components/Status/Status';
import Frame from '../layout/Frame';
import {
  AppRoute,
  ButtonType,
  StatusType,
  WebsiteHealthKey,
  WebsiteStatusSearch
} from '../types/enums';
import {
  StateAction,
  Website,
  WebsiteStaticData,
  WebsiteStaticDataHealth,
  WebsiteWithStaticData
} from '../types/types';
import { deleteWebsite } from '../requests/requests';
import useErrorHandler from '../hooks/useErrorHandler';
import useFilter from '../hooks/useFilter';
import AddNew from '../components/AddNew/AddNew';
import LimitReachedPopup from '../components/LimitReachedPopup/LimitReachedPopup';
import Panel from '../layout/Panel';
import { WebsitesInfo } from '../components/WebsitesInfo/WebsitesInfo';
import { AdminDropdown } from '../components/Input/Dropdown';
import { getDisplayUrl } from '../utils/common';
import Tooltip from '../components/Tooltip/Tooltip';

const Websites = () => {
  const navigate = useNavigate();
  const errorHandler = useErrorHandler();
  const { user } = useContext(UserContext);
  const { websites, staticData, refreshWebsites, refreshDashboards } = useContext(DataContext);
  const [selectedForDelete, setSelectedForDelete] = useState<WebsiteWithStaticData>();
  const [query, setQuery] = useState('');
  const [statusSelected, setStatusSelected] = useState(WebsiteStatusSearch.ALL);
  const sitesData = useMemo(
    () => mergeWebsitesData(websites, staticData.monitors),
    [websites, staticData]
  );
  const filteredWebsites = useFilter(sitesData, query);
  const filteredWebsitesByStatus = useMemo(() => {
    if (statusSelected === WebsiteStatusSearch.ALL) {
      return filteredWebsites;
    }

    return filteredWebsites.filter(({ health }) =>
      Object.values(health).some((status) => status === statusSelected)
    );
  }, [filteredWebsites, statusSelected]);
  const [showLimitPopup, setShowLimitPopup] = useState(false);

  const canAddNewWebsite = useMemo(
    () => websites.length < (user?.plan.websites ?? 0),
    [websites, user]
  );

  useEffect(() => {
    if (websites.length < 1) {
      refreshWebsites();
    }
  }, []);

  const onDelete = useCallback(() => {
    if (selectedForDelete?.id) {
      deleteWebsite(selectedForDelete.id)
        .then(() => {
          refreshWebsites();
          refreshDashboards();
        }, errorHandler)
        .finally(() => {
          setSelectedForDelete(undefined);
        });
    }
  }, [selectedForDelete]);

  const renderStatusSearch = useCallback(
    () => (
      <AdminDropdown
        options={STATUS_OPTIONS}
        value={statusSelected}
        setValue={setStatusSelected}
        id="website-status-search"
      />
    ),
    [statusSelected, setStatusSelected]
  );

  return (
    <Frame>
      <Panel>
        <h1>Websites</h1>
        <p>MonitorMonk will monitor the following websites, and&nbsp;
          <Tooltip opener="regularly report their status">
            We check your websites once every 1 minute.
            We report their status here and on your Dashboards.
          </Tooltip>.
          Use the list below to get an overview of your websites and to manage them.
          Use the <a target="_blank" rel="noreferrer" href={AppRoute.DASHBOARDS}>Dashboards</a> to display the status of your websites in a more visual way and to get alerts.
        </p>
        <WebsitesInfo
          rowHeaderRenderer={Resource}
          data={filteredWebsitesByStatus}
          renderers={[HTTPStatus, Keyword, SiteSpeed, SSLStatus]}
          actionRenderer={Actions({ delete: setSelectedForDelete })}
          search={query}
          setSearch={setQuery}
          renderAdditionalFilters={renderStatusSearch}
          renderAction={
            <AddNew
              title="Add new website"
              action={
                canAddNewWebsite
                  ? () => navigate(AppRoute.WEBSITE_NEW)
                  : () => setShowLimitPopup(true)
              }
            />
          }
        />
        {!!selectedForDelete && (
          <DeletePopup
            onCancel={() => setSelectedForDelete(undefined)}
            onDelete={onDelete}
            title={`You'll delete "${selectedForDelete?.name}" permanently.`}
          />
        )}
        {!!showLimitPopup && <LimitReachedPopup hide={() => setShowLimitPopup(false)} />}
      </Panel>
    </Frame>
  );
};

const baseHealth: WebsiteStaticDataHealth = {
  httpStatus: 'IGNORE',
  keywords: 'IGNORE',
  loadSpeed: 'IGNORE',
  sslStatus: 'IGNORE'
};

const mergeWebsitesData = (
  sites: Website[],
  staticData: WebsiteStaticData[]
): WebsiteWithStaticData[] => {
  return sites.map((site) => {
    const data = staticData.find((it) => it.id === site.id);
    const defaultHealth = {
      ...baseHealth,
      ...Object.fromEntries((site.attributesToCheck || []).map((it) => [it, 'NA']))
    };
    const health: WebsiteStaticDataHealth = data
      ? calculateHealthStatuses(site, data, defaultHealth)
      : defaultHealth;

    return {
      ...site,
      health
    };
  });
};

const calculateHealthStatuses = (
  site: Website,
  data: WebsiteStaticData,
  defaultHealth: WebsiteStaticDataHealth
) =>
  [
    WebsiteHealthKey.HTTP_STATUS,
    WebsiteHealthKey.KEYWORDS,
    WebsiteHealthKey.LOAD_SPEED,
    WebsiteHealthKey.SSL_STATUS
  ].reduce((siteHealth, attr) => {
    const siteAttrLastUpdated = Object.values(site.settings ?? {}).find(
      (it) => it.attribute === attr
    )?.updatedAt;

    if (
      siteAttrLastUpdated &&
      data?.lastCheckedDateTimeUtc &&
      isDateNewer(siteAttrLastUpdated, data.lastCheckedDateTimeUtc)
    ) {
      return { ...siteHealth, [attr]: data?.health[attr] };
    }

    return siteHealth;
  }, defaultHealth);

const isDateNewer = (old: string, newDate: string) => {
  const date1 = new Date(old);
  const date2 = new Date(newDate);

  return date1.getTime() < date2.getTime();
};

const Resource: React.FC<WebsiteWithStaticData> = ({ name, url }) => {
  const displayUrl = getDisplayUrl(url);

  return (
    <>
      <strong>{name}</strong>
      <a target="_blank" rel="noreferrer" href={url}>
        {displayUrl}
      </a>
    </>
  );
};

interface StatusRenderer extends WebsiteWithStaticData {
  title?: string;
}

const HTTPStatus: React.FC<StatusRenderer> = ({ health, title }) => {
  return (
    <Status
      icon={iconByWebsiteStatus[StatusType[health.httpStatus]]}
      value={health.httpStatus}
      type={health.httpStatus}
      title={title}
    />
  );
};

const Keyword: React.FC<StatusRenderer> = ({ health, title }) => {
  return (
    <Status
      icon={iconByWebsiteStatus[StatusType[health.keywords]]}
      value={health.keywords}
      type={health.keywords}
      title={title}
    />
  );
};

const SiteSpeed: React.FC<StatusRenderer> = ({ health, title }) => {
  return (
    <Status
      icon={iconByWebsiteStatus[StatusType[health.loadSpeed]]}
      value={health.loadSpeed}
      type={health.loadSpeed}
      title={title}
    />
  );
};

const SSLStatus: React.FC<StatusRenderer> = ({ health, title }) => {
  return (
    <Status
      icon={iconByWebsiteStatus[StatusType[health.sslStatus]]}
      value={health.sslStatus}
      type={health.sslStatus}
      title={title}
    />
  );
};

export const websiteRenderers = {
  httpStatus: HTTPStatus,
  keyword: Keyword,
  siteSpeed: SiteSpeed,
  sslStatus: SSLStatus
};

type Delete = StateAction<WebsiteWithStaticData | undefined>;

const Actions: (actions: { delete: Delete }) => React.FC<WebsiteWithStaticData> =
  (actions: { delete: Delete }) => (site) => {
    return (
      <>
        <NavLink to={generatePath(AppRoute.WEBSITE_EDIT, { id: site.id })}>
          <Button type={ButtonType.GHOST} spaceSaving small>
            edit
          </Button>
        </NavLink>
        <Button action={() => actions.delete(site)} type={ButtonType.GHOST} spaceSaving small>
          delete
        </Button>
      </>
    );
  };

const STATUS_OPTIONS = [
  {
    name: 'ALL STATUSES',
    value: WebsiteStatusSearch.ALL
  },
  {
    name: 'ERROR',
    value: WebsiteStatusSearch.ERROR
  },
  {
    name: 'WARNING',
    value: WebsiteStatusSearch.WARNING
  },
  {
    name: 'UNAVAILABLE',
    value: WebsiteStatusSearch.UNAVAILABLE
  },
  {
    name: 'OK',
    value: WebsiteStatusSearch.OK
  }
];

export default Websites;
